/**
 * Narrows a print's legend to what the print extent actually shows.
 *
 * Without this, the legend listed every class a layer's renderer COULD
 * draw, whether or not any feature of that class fell inside the printed
 * area — a layer with fifteen unique-value classes printed all fifteen even
 * when the chosen extent only ever showed three of them, and a layer with
 * nothing at all inside the box still got a row.
 */

import { prepare, relate, type Bbox } from './spatialRelate';
import type { Layer, LayerRenderer } from '../types';

/** The catch-all row's key, alongside real category values in the same set. */
export const OTHER_CATEGORY = '__other__';

function bboxPolygon(bbox: Bbox): GeoJSON.Polygon {
  return {
    type: 'Polygon',
    coordinates: [
      [
        [bbox.minX, bbox.minY],
        [bbox.maxX, bbox.minY],
        [bbox.maxX, bbox.maxY],
        [bbox.minX, bbox.maxY],
        [bbox.minX, bbox.minY],
      ],
    ],
  };
}

/** Mirrors MapViewOL's own `resolveFeatureColor` matching exactly, but
 *  returns the matched category's OWN value (or `OTHER_CATEGORY` for the
 *  catch-all row) instead of a colour — which legend row a feature belongs
 *  to, not what it should be painted. */
function matchedCategoryValue(renderer: LayerRenderer, feature: GeoJSON.Feature): string {
  if (renderer.type !== 'unique' || !renderer.field) return OTHER_CATEGORY;
  const properties = (feature.properties ?? {}) as Record<string, unknown>;
  let value = properties[renderer.field];
  if (value === undefined) {
    const key = Object.keys(properties).find(
      (candidate) => candidate.toLowerCase() === renderer.field!.toLowerCase(),
    );
    if (key) value = properties[key];
  }
  if (value === null || value === undefined) return OTHER_CATEGORY;
  const asString = String(value).trim();
  const match = renderer.categories?.find((category) => category.value.trim() === asString);
  return match ? match.value.trim() : OTHER_CATEGORY;
}

export interface PrintLegendFilter {
  /** Only the layers (from the ones given, same order) with at least one
   *  feature inside the print extent — the rest should not appear in the
   *  legend at all. */
  visibleLayers: Layer[];
  /** Category values actually present within the extent, by layer id — a
   *  layer with no entry here was not checked (its symbology comes from the
   *  service's own rendered legend image, which this cannot see inside)
   *  and keeps every class, the original behaviour. */
  presentCategoryValues: Map<string, Set<string>>;
}

/**
 * Checks every layer this app can read feature geometry for — local data,
 * an ArcGIS FeatureServer, or a renderer the app generated an SLD from
 * (which implies a same-named GeoServer WFS feature type) — against the
 * print extent, and keeps only what actually has something inside it.
 *
 * A layer with no renderer and no locally/FeatureServer-held geometry is a
 * service-rendered legend image (ArcGIS MapServer, plain WMS) — there is no
 * per-feature geometry to check client-side, so it is left exactly as it
 * was: always included, every class shown.
 *
 * `fetchFeatures` is the caller's own feature-loading function (App.tsx
 * already has one wired to the right service per layer kind) — kept as a
 * parameter rather than imported directly so this file stays plain spatial
 * logic, not another copy of the service-fetching branches.
 */
export async function filterLegendToPrintExtent(
  layers: Layer[],
  extent: Bbox,
  fetchFeatures: (layer: Layer) => Promise<GeoJSON.Feature[]>,
): Promise<PrintLegendFilter> {
  const box = prepare(bboxPolygon(extent));
  const presentCategoryValues = new Map<string, Set<string>>();
  const keep = new Set<string>();

  await Promise.all(
    layers.map(async (layer) => {
      const checkable = Boolean(layer.renderer) || Boolean(layer.localData) || layer.arcgisLayerId !== undefined;
      if (!checkable) {
        keep.add(layer.id);
        return;
      }

      let features: GeoJSON.Feature[];
      try {
        features = await fetchFeatures(layer);
      } catch {
        // A fetch failure should not silently drop an otherwise-legitimate
        // legend entry — fall back to including it unfiltered.
        keep.add(layer.id);
        return;
      }

      // Local data and GeoServer WFS don't narrow by bbox server-side (see
      // featureLoad.ts's own doc comment on why WFS never sends one) — every
      // source is re-checked here regardless, so one test covers all three.
      const within = features.filter((feature) => {
        if (!feature.geometry) return false;
        const geometry = prepare(feature.geometry);
        return geometry.positions.length > 0 && relate(geometry, box, 'intersect', 0, 1);
      });

      if (within.length === 0) return; // nothing of this layer is in the print area

      keep.add(layer.id);
      if (layer.renderer?.type === 'unique') {
        const renderer = layer.renderer;
        presentCategoryValues.set(layer.id, new Set(within.map((feature) => matchedCategoryValue(renderer, feature))));
      }
    }),
  );

  return { visibleLayers: layers.filter((layer) => keep.has(layer.id)), presentCategoryValues };
}
